import { create } from 'zustand';

import type { AdminDataScope, Menu } from '@/api/rbac';
import { prunePlatformOnlyMenus, stripPlatformOnlyCodes } from './dataScope';
import { useAuthStore } from './auth';
import { loginSessionOf } from './loginSession';

/**
 * RBAC 权限状态。
 *
 * 数据来源：`PermissionsBootstrap` 在登录后调 `/admin/rbac/me`，
 * 用 `collectPermissionCodes(menus)` 收集菜单树里所有 permission_code
 * （树里混着后端补父链拼出来的脚手架节点，取舍见该函数注释），
 * 写入这里的 `permissions` 集合；`super_admin` 标志直接来自接口。
 *
 * 数据范围（data_scope）同样来自 /me，只放内存、不持久化：SUPER_ADMIN 随时可能改账号范围，
 * 后端下一次请求就按新范围执行，前端靠 PermissionsBootstrap 的重新对齐跟上。
 * COMPANY 账号在写入时就剔掉平台专用的权限码与菜单（见 ./dataScope），
 * 页面里的 has() / IfHas / 侧边栏都不用再单独判断。
 */
export interface DataScopeInfo {
  dataScope: AdminDataScope;
  companyId: number | null;
}
export interface PermissionsState {
  /** 已加载（PermissionsBootstrap 调通 /me 后置 true） */
  loaded: boolean;
  /** 是否 SUPER_ADMIN — 跳过所有 gate */
  superAdmin: boolean;
  /** 是否因 /admin/rbac/me 首次请求失败而启用旧后端兼容模式 */
  degraded: boolean;
  /** 权限码集合（如 'admin:user:read'） */
  permissions: Set<string>;
  /** 当前用户可见的菜单树（来自 /admin/rbac/me，驱动侧边栏动态渲染） */
  menus: Menu[];
  /** 数据范围；未加载 / 旧后端不返回时按 PLATFORM */
  dataScope: AdminDataScope;
  /** COMPANY 账号的所属公司 */
  companyId: number | null;

  setLoaded: (v: boolean) => void;
  setPermissions: (
    perms: string[],
    superAdmin: boolean,
    menus?: Menu[],
    scope?: DataScopeInfo,
  ) => void;
  setDegradedFallback: () => void;
  /** 仅供 dev：在控制台或测试里临时覆盖权限集，验证 gate 显隐 */
  __devSet: (perms: string[], superAdmin?: boolean) => void;
  clear: () => void;
}

export const usePermissionsStore = create<PermissionsState>((set) => ({
  // 初始 loaded=false：路由 guard 看到这个会显示 spinner 直到 /me 返回。
  // PermissionsBootstrap 拿到响应后调 setPermissions 写入真实权限集。
  loaded: false,
  superAdmin: false,
  degraded: false,
  permissions: new Set<string>(),
  menus: [],
  dataScope: 'PLATFORM',
  companyId: null,

  setLoaded: (v) => set({ loaded: v }),
  setPermissions: (perms, superAdmin, menus = [], scope = { dataScope: 'PLATFORM', companyId: null }) => {
    // SUPER_ADMIN 后端恒为平台视角，这里也不让一个异常的 COMPANY 值把超管的入口剔掉。
    const company = !superAdmin && scope.dataScope === 'COMPANY';
    set({
      permissions: new Set(company ? stripPlatformOnlyCodes(perms) : perms),
      superAdmin,
      degraded: false,
      menus: company ? prunePlatformOnlyMenus(menus) : menus,
      dataScope: company ? 'COMPANY' : 'PLATFORM',
      companyId: company ? scope.companyId : null,
      loaded: true,
    });
  },
  setDegradedFallback: () =>
    set({
      permissions: new Set(),
      superAdmin: true,
      degraded: true,
      menus: [],
      dataScope: 'PLATFORM',
      companyId: null,
      loaded: true,
    }),
  __devSet: (perms, superAdmin = false) =>
    set({ permissions: new Set(perms), superAdmin, degraded: false, loaded: true }),
  clear: () =>
    set({
      loaded: false,
      superAdmin: false,
      degraded: false,
      permissions: new Set(),
      menus: [],
      dataScope: 'PLATFORM',
      companyId: null,
    }),
}));

// 换账号或同账号重新登录时同步清除旧权限，避免新页面在 /me 返回前沿用旧会话权限。
// 正常 Token 刷新沿用会话编号，不清除权限，也不造成页面闪退。
useAuthStore.subscribe((state, previous) => {
  if (loginSessionOf(state) !== loginSessionOf(previous)) usePermissionsStore.getState().clear();
});

/** 非 React 上下文用（如 router guard）。 */
export const hasPermission = (code: string): boolean => {
  const s = usePermissionsStore.getState();
  return s.superAdmin || s.permissions.has(code);
};

/** 当前是否公司账号（非 React 上下文用）。 */
export const isCompanyScope = (): boolean =>
  usePermissionsStore.getState().dataScope === 'COMPANY';

/** hasPermission 的简写别名，对齐后端文档术语。 */
export const hasPerm = hasPermission;

/** 任一即可 */
export const hasAnyPerm = (...codes: string[]): boolean => {
  const s = usePermissionsStore.getState();
  return s.superAdmin || codes.some((c) => s.permissions.has(c));
};

/* ============================================================
 * 重新拉取 /admin/rbac/me 的回调注册表
 *
 * 管理员在别处改了角色授权后，已登录用户的这份权限集是陈旧的（只在 token 变化时拉）。
 * PermissionsBootstrap 挂载时把自己的 revalidate 注册进来，供两类非 React 上下文触发：
 *   1. 响应拦截器收到 403（前端以为有权限、后端拒绝）→ 说明本地权限已过期；
 *   2. 将来任何需要"立刻重新对齐权限"的地方。
 * 放在 store 里（而不是 api/client.ts）是为了避免 client ↔ api/rbac 的循环依赖。
 * ========================================================== */

type Revalidator = () => void;

let revalidator: Revalidator | null = null;

/** PermissionsBootstrap 专用：注册 / 注销。返回注销函数便于在 effect 里 cleanup。 */
export const registerPermissionsRevalidator = (fn: Revalidator): (() => void) => {
  revalidator = fn;
  return () => {
    if (revalidator === fn) revalidator = null;
  };
};

/** 请求重新拉一次 /me。未登录或组件未挂载时是空操作；节流由注册方负责。 */
export const revalidatePermissions = (): void => {
  revalidator?.();
};

/** Dev helper — 暴露到全局方便控制台调试。 */
if (typeof window !== 'undefined') {
  (window as unknown as { __setPermissions?: (perms: string[], superAdmin?: boolean) => void })
    .__setPermissions = (perms, superAdmin) =>
      usePermissionsStore.getState().__devSet(perms, superAdmin);
}
